import React, { useMemo, useRef, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioButton,
} from '../../components/BrioComponents';
import { parseMeasurementSheet, buildZonePathLookup } from '../utils/measurementSheetXlsx';

const STAGE = { PICK: 'pick', PARSING: 'parsing', REVIEW: 'review', IMPORTING: 'importing' };

// Append measurements to a line's take-off from an .xlsx (the format the
// per-line "Export Excel" produces). Geometry only — the four measures are
// re-derived by the DB, so formula columns in the file are ignored.
function ImportMeasurementsModal({ line, zonesById, onClose, onImport }) {
  const [stage, setStage] = useState(STAGE.PICK);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const fileRef = useRef(null);

  const zonesByPath = useMemo(() => buildZonePathLookup(zonesById), [zonesById]);
  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);
  const invalidCount = rows.length - validRows.length;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setStage(STAGE.PARSING);
    setSubmitError(null);
    try {
      const result = await parseMeasurementSheet(file, { zonesByPath });
      setRows(result.rows);
      setStage(STAGE.REVIEW);
    } catch (err) {
      console.error('[ImportMeasurementsModal] parse failed', err);
      setSubmitError(`Couldn't read the file: ${err.message}`);
      setStage(STAGE.PICK);
    }
  };

  const handleConfirm = async () => {
    if (validRows.length === 0) return;
    setStage(STAGE.IMPORTING);
    setSubmitError(null);
    const inputs = validRows.map((r) => ({
      description: r.description,
      nr: r.nr,
      length: r.length,
      breadth: r.breadth,
      height: r.height,
      projectZoneId: r.projectZoneId,
      level: r.level,
      gridRef: r.gridRef,
      locationNote: r.locationNote,
    }));
    const result = await onImport(inputs);
    if (result.success) onClose();
    else { setSubmitError(result.error || 'Import failed.'); setStage(STAGE.REVIEW); }
  };

  let footer;
  if (stage === STAGE.PICK || stage === STAGE.PARSING) {
    footer = <BrioButton variant="secondary" onClick={onClose}>Cancel</BrioButton>;
  } else {
    footer = (
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={stage === STAGE.IMPORTING}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={handleConfirm} disabled={validRows.length === 0 || stage === STAGE.IMPORTING}>
          {stage === STAGE.IMPORTING ? 'Importing…' : invalidCount > 0 ? `Add ${validRows.length} valid (skip ${invalidCount})` : `Add ${validRows.length}`}
        </BrioButton>
      </>
    );
  }

  return (
    <BrioModal title={`Import take-off for ${line.version?.fullCode || ''}`} onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {submitError && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>{submitError}</div>
        )}

        {stage === STAGE.PICK && (
          <div>
            <p style={{ marginTop: 0 }}>
              Pick an <code>.xlsx</code> in the take-off format (Description, Nr, L, B, H, Zone, Level, Grid, Note).
              Easiest is to <strong>Export Excel</strong> from this line first, extend it in Excel, and import it back.
              The four measure columns are recalculated automatically — anything you put there is ignored.
            </p>
            <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFile} style={{ display: 'none' }} />
            <BrioButton variant="primary" onClick={() => fileRef.current?.click()}>Choose Excel file…</BrioButton>
          </div>
        )}

        {stage === STAGE.PARSING && (
          <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--brio-text-muted)' }}>Reading {fileName}…</div>
        )}

        {(stage === STAGE.REVIEW || stage === STAGE.IMPORTING) && (
          <div>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              <strong>{rows.length}</strong> row{rows.length === 1 ? '' : 's'} from <code>{fileName}</code>:
              &nbsp;<span style={{ color: '#2e7d32' }}>{validRows.length} valid</span>
              {invalidCount > 0 && <span>, <span style={{ color: '#a94442' }}>{invalidCount} with errors</span></span>}.
            </div>
            <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.35rem 0.5rem', width: 30 }} />
                    <th style={{ padding: '0.35rem 0.5rem' }}>Description</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Nr</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>L</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>B</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>H</th>
                    <th style={{ padding: '0.35rem 0.5rem' }}>Zone</th>
                    <th style={{ padding: '0.35rem 0.5rem' }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const ok = r.errors.length === 0;
                    return (
                      <tr key={r.rowNumber} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', background: ok ? 'transparent' : '#fdecec' }}>
                        <td style={{ padding: '0.3rem 0.5rem', color: ok ? '#2e7d32' : '#a94442' }}>{ok ? '✓' : '✗'}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{r.description || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{r.nr ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{r.length ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{r.breadth ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{r.height ?? '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{r.zonePath || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', color: '#a94442', fontSize: '0.76rem' }}>{r.errors.join(' · ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default ImportMeasurementsModal;
